/**
 * `parsers.ts` — Interview Analysis utility functions (B-13).
 *
 * Pure functions ported verbatim from the prototype canon at
 * `~/certain-prototype/public/pipeline.html:545-825`, with TypeScript types
 * added and the font/color substitutions ratified by HoD (Geist, CSS-var tokens).
 *
 * Disambiguation note (IMPERATIVE — do not confuse these two tables):
 *
 *   • `SECTION_COLORS` (this file, prototype L721-729):
 *     Substring-match table used INSIDE `renderAnalysisCards` to color
 *     Detailed Assessment nav items on the **post-interview** Analysis tab.
 *     Keys are lowercase substrings; default is FIT_COLOR_BLUE (#3B82F6).
 *
 *   • `EVAL_SECTIONS` (prototype L545-553, AI Evaluation tab canon):
 *     A DIFFERENT regex array used by `evalSectionDef()` on the
 *     **pre-interview** AI Eval tab to choose a color + icon per section
 *     heading. That array does NOT live in this module. This module is
 *     Analysis-tab-only (post-interview).
 *
 * These two tables exist for the same visual purpose (section color) but on
 * different tabs with different data shapes. Mixing them is a REWORK trigger.
 *
 * All parsers are pure (no React imports, no hooks). Callers memoize.
 */

import {
  VERDICT_COLORS as VERDICT_COLORS_TOKENS,
  SECTION_COLORS as SECTION_COLORS_TOKENS,
  VERDICT_NULL_COLOR,
  FIT_COLOR_BLUE,
  FIT_COLOR_WARN,
} from "../tokens";

// ── Scorecard types ───────────────────────────────────────────────────────────

type ScorecardVerdict =
  | "POSITIVE"
  | "STRONG"
  | "MIXED"
  | "WATCH"
  | "WEAK"
  | "GAP"
  | string; // passthrough for unknown verdict labels

export interface ScorecardRow {
  dim: string;
  signal: string;
  verdict: ScorecardVerdict;
}

// ── Verdict / color tables (prototype L569-593) ───────────────────────────────

/**
 * Scorecard cell coloring (prototype L569-574).
 * Maps normalised UPPERCASE verdict to a CSS hex color.
 * Imported from tokens.ts — single source of truth for these literals.
 */
export const VERDICT_COLORS: Record<string, string> = VERDICT_COLORS_TOKENS;

/**
 * Scorecard star counts (prototype L588-593).
 * 5-star scale: POSITIVE/STRONG=5, MIXED=3, WATCH/WEAK=2, GAP=1.
 */
export const VERDICT_STARS: Record<string, number> = {
  POSITIVE: 5,
  STRONG: 5,
  MIXED: 3,
  WATCH: 2,
  WEAK: 2,
  GAP: 1,
} as const;

// ── Post-interview verdict phrases (prototype L558) ───────────────────────────

/**
 * Ordered verdict phrases for `extractVerdict` (prototype L557-559).
 * ORDER MATTERS — longer phrases must precede their substrings so
 * "Strong Hire" is not split as "Hire".
 */
const VERDICTS = [
  "Strong Hire",
  "Strong Pass",
  "Strong Proceed",
  "Proceed with Caution",
  "Hire",
  "Pass",
  "Proceed",
] as const;

export type Verdict = (typeof VERDICTS)[number];

// ── SECTION_COLORS (prototype L721-729) ──────────────────────────────────────
//
// IMPORTANT: This is the Detailed Assessment nav coloring table for the
// post-interview Analysis tab. It is NOT the same as the EVAL_SECTIONS
// regex array used by the pre-interview AI Eval tab (prototype L545-553).
// See module-level disambiguation note.
//
// Lowercase substring match. First match wins (insertion-order iteration).
// Default when no pattern matches: FIT_COLOR_BLUE (#3B82F6).
//
const SECTION_COLORS: Record<string, string> = SECTION_COLORS_TOKENS;

// ── Section abbreviation map (prototype L749-758) ─────────────────────────────

/**
 * Hard-coded abbreviation map for canonical Analysis section titles.
 * Both slash-spacing variants for "Technical Depth" are included.
 */
const SECTION_ABBREVIATIONS: Record<string, string> = {
  "Candidate Overview": "Overview",
  "Key Strengths": "Strengths",
  "Gaps & Areas to Probe": "Gaps & Probes",
  "Technical / Domain Depth Assessment": "Tech Depth", // both slash variants
  "Technical/Domain Depth Assessment": "Tech Depth",
  "Tenure & Stability Assessment": "Tenure & Stability",
  "Pre-Interview Recommendation": "Recommendation",
  "Bottom Line": "Bottom Line",
};

// ── Exported parser functions ─────────────────────────────────────────────────

/**
 * Parse the `## Scorecard` block into typed rows (prototype L576-585).
 *
 * Regex anchors on `## Scorecard\n` and captures until the next H2/H3 or
 * end-of-string. Each line that contains `|` is split on `|`; rows with
 * fewer than 3 pipe-separated parts are dropped.
 *
 * Returns an empty array when no Scorecard block is found.
 */
export function parseScorecardRows(text: string): ScorecardRow[] {
  const m = text.match(/## Scorecard\n([\s\S]*?)(?=\n## |\n###|$)/);
  if (!m || !m[1]) return [];
  return m[1]
    .trim()
    .split("\n")
    .filter((l) => l.includes("|"))
    .map((l) => {
      const parts = l.split("|").map((s) => s.trim());
      if (parts.length < 3) return null;
      return {
        dim: parts[0] ?? "",
        signal: parts[1] ?? "",
        verdict: (parts[2] ?? "").toUpperCase(),
      } satisfies ScorecardRow;
    })
    .filter((r): r is ScorecardRow => r !== null);
}

/**
 * Parse the analysis text into a map of section headings → section bodies
 * (prototype L620-631).
 *
 * Strips the `## Scorecard` block first (handled separately by
 * `parseScorecardRows`). Splits on `\n## ` boundaries; key is the heading
 * text trimmed, value is the body trimmed.
 *
 * Returns an empty object when no H2 sections are present.
 */
export function parseEvalSections(text: string): Record<string, string> {
  // Strip scorecard section — handled separately.
  const stripped = text
    .replace(/## Scorecard\n[\s\S]*?(?=\n## |\n###|$)/, "")
    .trim();

  const out: Record<string, string> = {};
  const parts = stripped.split(/\n(?=## )/);
  for (const part of parts) {
    const m = part.match(/^## (.+)\n([\s\S]*)/);
    if (!m || !m[1]) continue;
    out[m[1].trim()] = (m[2] ?? "").trim();
  }
  return out;
}

/**
 * Extract the first matching verdict phrase from text (prototype L557-560).
 *
 * Tries each phrase in VERDICTS order so "Strong Hire" matches before
 * "Hire". Returns null when no phrase is found.
 */
export function extractVerdict(text: string): Verdict | null {
  for (const v of VERDICTS) {
    if (text.includes(v)) return v;
  }
  return null;
}

/**
 * Map a verdict phrase to a hex color (prototype L562-567).
 *
 * Null verdict → slate (VERDICT_NULL_COLOR = #94A3B8).
 * Strong Hire / Strong Proceed → emerald (SECTION_COLORS.strengths = #10B981).
 * Hire / Proceed (bare) → blue (FIT_COLOR_BLUE = #3B82F6).
 * Proceed with Caution → amber (FIT_COLOR_WARN = #F59E0B).
 * Pass / Strong Pass → red (VERDICT_COLORS.GAP = #EF4444).
 */
export function verdictColor(v: Verdict | null): string {
  if (!v) return VERDICT_NULL_COLOR;
  if (/Strong Hire|Strong Proceed/.test(v)) return SECTION_COLORS["strengths"]!;
  if (/Hire|Proceed$/.test(v)) return FIT_COLOR_BLUE;
  if (/Caution/.test(v)) return FIT_COLOR_WARN;
  return VERDICT_COLORS_TOKENS.GAP;
}

// ── Conversation Health (model-derived interaction-health signal) ─────────────
//
// REPLACES the old keyword `sentiment()` (a bag-of-words that counted
// positive/negative WORDS in the raw transcript — fake, and it contradicted the
// verdict). The real signal is emitted BY THE MODEL as a `## Conversation Health`
// section of the post-interview analysis (backend prompt
// `INTERVIEW_ANALYSIS_POST_SYSTEM` v2). It measures the HEALTH of the
// interviewer↔candidate EXCHANGE — warmth, two-way engagement, how openly hard
// topics were handled — explicitly NOT the candidate's likability / culture-fit /
// personality (those encode affinity bias; see the prompt's bias framing).

type HealthRating = "Healthy" | "Mixed" | "Strained";

interface ConversationHealth {
  rating: HealthRating;
  score: number; // 0–100, model-derived interaction-health score
  read: string; // short qualitative read of the interaction dynamic
}

const _HEALTH_RATINGS: Record<string, HealthRating> = {
  healthy: "Healthy",
  mixed: "Mixed",
  strained: "Strained",
};

/**
 * Parse the `## Conversation Health` section out of post-interview analysis
 * markdown.
 *
 * The first line is a structured marker `Rating: [Healthy|Mixed|Strained] (<0-100>)`;
 * the remaining lines are the model's short qualitative read.
 *
 * Defensive / backfill-tolerant: returns `null` (never throws) when the section
 * is absent, has no rating marker, or the score is out of range — analyses
 * generated before prompt v2 simply have no signal, and callers hide the surface
 * when this is null. Mirrors the backend `parse_conversation_health`.
 */
export function parseConversationHealth(text: string): ConversationHealth | null {
  if (!text) return null;

  const section = text.match(
    /## Conversation Health\s*\n([\s\S]*?)(?=\n## |\n###|$)/i,
  );
  if (!section || !section[1]) return null;
  const body = section[1].trim();
  if (!body) return null;

  const rating = body.match(
    /Rating:\s*\[?\s*(healthy|mixed|strained)\s*\]?\s*\(\s*(\d{1,3})\s*\)/i,
  );
  if (!rating || !rating[1] || !rating[2]) return null;

  const label = _HEALTH_RATINGS[rating[1].toLowerCase()];
  if (!label) return null;

  const score = parseInt(rating[2], 10);
  if (Number.isNaN(score) || score < 0 || score > 100) return null;

  const read = body
    .slice((rating.index ?? 0) + rating[0].length)
    .trim()
    .replace(/^\[/, "")
    .replace(/\]$/, "")
    .trim();

  return { rating: label, score, read };
}

/**
 * Color token for a health rating — semantic brand tokens (tokens.css),
 * NOT raw hex. Each var resolves to the same hex it replaced:
 * Healthy → --verdict-positive-fg (#00C896); Mixed → --verdict-watch-fg
 * (#F59E0B); Strained → --verdict-gap-fg (#EF4444).
 */
export function healthColor(rating: HealthRating): string {
  if (rating === "Healthy") return "var(--verdict-positive-fg)";
  if (rating === "Mixed") return "var(--verdict-watch-fg)";
  return "var(--verdict-gap-fg)";
}

/**
 * Extract the FINAL VERDICT sentence from a recommendation body
 * (prototype L681).
 *
 * Returns the sentence(s) after `**FINAL VERDICT**\n` up to the next
 * bold-marker or end-of-string. Returns null when the pattern is absent.
 */
export function extractFinalVerdictSentence(
  recommendationBody: string,
): string | null {
  const m = recommendationBody.match(
    /\*\*FINAL VERDICT\*\*\s*\n([\s\S]*?)(?:\n\*\*|$)/i,
  );
  if (!m || !m[1]) return null;
  return m[1].trim() || null;
}

/**
 * Resolve a nav-item color for a section key (prototype L730-736).
 *
 * Lowercases the key and tests each entry in SECTION_COLORS for a
 * substring match (insertion-order, first match wins). Falls back to
 * FIT_COLOR_BLUE (#3B82F6, --mid) when no pattern matches.
 *
 * See disambiguation note at the top of this file: this is NOT the
 * pre-interview EVAL_SECTIONS regex array.
 */
export function sectionColor(key: string): string {
  const lk = key.toLowerCase();
  for (const [pat, col] of Object.entries(SECTION_COLORS)) {
    if (lk.includes(pat)) return col;
  }
  return FIT_COLOR_BLUE;
}

/**
 * Shorten a section heading to a compact nav label (prototype L738-761).
 *
 * Two cases:
 *   1. Numbered section `"1. The Demo: A Strong…"` →
 *      text before first colon OR first 4 words; truncated to 22 chars.
 *   2. Named section → abbreviation map lookup, then 22-char truncation.
 */
export function shortenSectionLabel(key: string): string {
  // Numbered sections (prototype L740-746).
  const numMatch = key.match(/^(\d+)\.\s*(.+)/);
  if (numMatch) {
    const body = numMatch[2] ?? "";
    const colonIdx = body.indexOf(":");
    const short =
      colonIdx > 0
        ? body.slice(0, colonIdx)
        : body.split(" ").slice(0, 4).join(" ");
    return short.length > 22 ? short.slice(0, 22) + "…" : short;
  }
  // Named sections (prototype L748-760).
  const abbrev = SECTION_ABBREVIATIONS[key];
  if (abbrev) return abbrev;
  return key.length > 22 ? key.slice(0, 22) + "…" : key;
}
